// Shared table behaviour. G360_K392
//
// Sort, filter, column selection and export - the four things every
// table in this console should have and almost none of them do.
//
// This is AgentsPage's implementation moved somewhere it can be reused,
// not a new design. Its decisions are kept deliberately:
//
//   EXPORT TAKES WHAT IS ON SCREEN. The filtered, sorted rows and the
//   visible columns. A person who filtered to 12 rows and opened a file
//   with 582 in it would be right to stop trusting the export, and a
//   person who exported a table with three columns hidden did not ask
//   for those columns.
//
//   BLANKS SORT LAST IN BOTH DIRECTIONS. Ascending by a sparse column
//   should not open with forty empty cells. That is not a sort result,
//   it is the data hidden behind the data.
//
//   COLUMNS HIDE BY POSITION, via a generated stylesheet keyed on
//   nth-child, so a header and its cells can never fall out of step the
//   way they do when markup is edited in two places.
//
//   THE IDENTIFYING COLUMN CANNOT BE HIDDEN. It is simply not offered
//   in the picker. A table whose name column is off is a grid of
//   attributes belonging to nobody.
//
// Every piece here is independent. A table can take the filter without
// the column picker, or sort without export. Nothing has to be adopted
// all at once, which is what makes retrofitting 162 tables survivable.

import { useEffect, useRef, useState } from 'react';
/* G360_K392E - every name here is one the console already imports
   elsewhere, so it is proven to exist in the pinned lucide-react. */
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, Download, SlidersHorizontal } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* SORT                                                                */
/* ------------------------------------------------------------------ */

/* Three states, not two: ascending, descending, and back to the
   table's own default order. The default is often meaningful - newest
   first, most-gaps-first, ranked by exposure - and a sort a person
   cannot undo has taken that away from them. */
export function useSort(defaultKey = null, defaultDir = 'asc') {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);
  function toggleSort(key) {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc');
  }
  return { sortKey, sortDir, toggleSort, setSortKey, setSortDir };
}

/* sortVal(row, key) returns the comparable value, or null for "this row
   has nothing here". Returning null rather than '' or 0 is what lets
   blanks sort last instead of masquerading as the smallest value. */
export function sortRows(rows, sortKey, sortDir, sortVal) {
  if (!sortKey || typeof sortVal !== 'function') return rows;
  const dir = sortDir === 'desc' ? -1 : 1;
  return rows.slice().sort((x, y) => {
    const a = sortVal(x, sortKey);
    const b = sortVal(y, sortKey);
    if ((a === null || a === undefined) && (b === null || b === undefined)) return 0;
    if (a === null || a === undefined) return 1;   // blanks last, ascending
    if (b === null || b === undefined) return -1;  // and descending
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }) * dir;
  });
}

export function SortTh({ k, label, sortKey, sortDir, onSort, style, title }) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onSort(k)}
      title={title || ('Sort by ' + String(label).toLowerCase())}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...(style || {}) }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {active
          ? (sortDir === 'asc'
              ? <ChevronUp size={13} style={{ color: 'var(--g-primary)' }} />
              : <ChevronDown size={13} style={{ color: 'var(--g-primary)' }} />)
          : <ChevronsUpDown size={13} style={{ color: 'var(--g-text-3)', opacity: 0.5 }} />}
      </span>
    </th>
  );
}

/* ------------------------------------------------------------------ */
/* FILTER                                                              */
/* ------------------------------------------------------------------ */

/* Deliberately NOT .ap-search. That class is the global menu search -
   a wide pill with a magnifier, in the top bar, which searches the
   whole application. Reusing it for a table filter puts two identical
   looking controls on one screen doing entirely different jobs, and
   people read the second one as another way to search the app.

   G360_K392E - the distinction is the SHAPE and the PLACEMENT, not the
   icon: the global search is a wide pill floating in the top bar, this
   is a bordered box sitting on the table it filters. */
export function TableFilter({ value, onChange, placeholder = 'Filter...', width = 240, count }) {
  return (
    <div
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        height: 32, padding: '0 10px', width,
        background: 'var(--g-surface)',
        border: '1px solid var(--g-border)',
        borderRadius: 'var(--g-r-sm)',
      }}
    >
      <Search size={14} style={{ color: 'var(--g-text-3)', flexShrink: 0 }} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{ flex: 1, minWidth: 0, border: 0, background: 'transparent', outline: 'none', fontSize: 13, font: 'inherit' }}
      />
      {value && (
        <button
          onClick={() => onChange('')}
          title="Clear filter"
          style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--g-text-3)', fontSize: 15, lineHeight: 1, padding: 0 }}
        >
          &times;
        </button>
      )}
      {typeof count === 'number' && !value && (
        <span style={{ fontSize: 11, color: 'var(--g-text-3)', flexShrink: 0 }}>{count}</span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* COLUMNS                                                             */
/* ------------------------------------------------------------------ */

/* columns: [{ key, n, label }] where n is the 1-based position of the
   column in the table. Position, not markup, is what gets hidden.

   The identifying column is simply absent from this array. Not
   disabled, not unchecked - absent, because offering to hide the only
   column that says which row you are looking at is not a real choice.

   storageKey scopes the preference per table, e.g.
   'g360.devices.hiddenCols'. localStorage is per browser and per
   person, which is the right scope for a view preference and the wrong
   one for anything that has to be reliable - so every access is
   wrapped, and a private window simply shows every column. */
export function useHiddenCols(storageKey, defaultHidden = []) {
  const [hidden, setHidden] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) { /* private mode: show everything */ }
    return defaultHidden;
  });

  function toggleCol(key) {
    setHidden((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : prev.concat(key);
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (e) { /* not fatal */ }
      return next;
    });
  }

  function showAll() {
    setHidden([]);
    try { localStorage.removeItem(storageKey); } catch (e) { /* not fatal */ }
  }

  return { hidden, toggleCol, showAll };
}

export function ColumnPicker({ columns, hidden, onToggle, onShowAll, alwaysLabel }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  /* Close on an outside click. A picker that only closes via its own
     Done button is a modal wearing a dropdown costume. */
  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) { if (box.current && !box.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const shownCount = columns.length - hidden.length;

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button
        className="btn outline"
        style={{ height: 32, padding: '0 12px', fontSize: 12 }}
        onClick={() => setOpen((o) => !o)}
        title="Choose which columns to show"
      >
        <SlidersHorizontal size={14} />
        Columns{hidden.length > 0 ? ' (' + shownCount + ' of ' + columns.length + ')' : ''}
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 36, zIndex: 30, minWidth: 220,
          background: 'var(--g-surface)', border: '1px solid var(--g-border)',
          borderRadius: 'var(--g-r-md)', boxShadow: 'var(--g-e2)', padding: '8px 10px',
        }}>
          {alwaysLabel && (
            <div style={{ fontSize: 11, color: 'var(--g-text-3)', marginBottom: 6 }}>{alwaysLabel}</div>
          )}
          {columns.map((c) => (
            <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={!hidden.includes(c.key)} onChange={() => onToggle(c.key)} />
              {c.label}
            </label>
          ))}
          <div style={{ borderTop: '1px solid var(--g-divider)', marginTop: 6, paddingTop: 6, display: 'flex', gap: 8 }}>
            <button className="btn outline" style={{ height: 26, padding: '0 8px', fontSize: 11 }} onClick={onShowAll}>Show all</button>
            <button className="btn outline" style={{ height: 26, padding: '0 8px', fontSize: 11 }} onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* Hides columns by position. tableClass must be a class unique to the
   one table, or this will hide the same column index in every table on
   the page. */
/* G360_K414 - warn once per table, not once per render. A duplicate
   position is a permanent property of a column list, so repeating it
   sixty times a second trains people to ignore the console. */
const _posWarned = new Set();

export function ColumnHideStyle({ tableClass, columns, hidden }) {
  /* G360_K414 - two columns claiming one position is silent and
     destructive: the generated nth-child rule matches both, so hiding
     one hides the other, and the CSV export goes out wrong with no
     error anywhere. DecisionsPage shipped with owner and state both
     at n: 7.

     Reported rather than thrown. A malformed column list should not
     blank a page a person is working in - but it must not stay
     invisible either. */
  const _cls = tableClass || 'table';
  if (!_posWarned.has(_cls)) {
    const seen = new Map();
    const dupes = [];
    const positions = [];
    (columns || []).forEach(function (c) {
      if (!c || typeof c.n !== 'number') return;
      positions.push(c.n);
      if (seen.has(c.n)) dupes.push('position ' + c.n + ' is claimed by both "' + seen.get(c.n) + '" and "' + c.key + '"');
      else seen.set(c.n, c.key);
    });
    const sorted = positions.slice().sort(function (a, b) { return a - b; });
    const gaps = sorted.length > 0 && (sorted[0] !== 1 || sorted[sorted.length - 1] !== sorted.length);
    if (dupes.length > 0 || gaps) {
      _posWarned.add(_cls);
      if (typeof console !== 'undefined' && console.error) {
        console.error(
          '[g360 table] ' + _cls + ': column positions are wrong. ' +
          (dupes.length > 0 ? dupes.join('; ') + '. ' : '') +
          (gaps ? 'Positions must run 1..N with no gaps; got [' + sorted.join(', ') + ']. ' : '') +
          'Hiding a column will hide the wrong cells, and the CSV export will not match the screen.'
        );
      }
    }
  }
  if (!hidden || hidden.length === 0) return null;
  const css = hidden
    .map((k) => (columns.find((c) => c.key === k) || {}).n)
    .filter(Boolean)
    .map((n) => '.' + tableClass + ' th:nth-child(' + n + '),.' + tableClass + ' td:nth-child(' + n + '){display:none}')
    .join('');
  if (!css) return null;
  return <style>{css}</style>;
}

/* ------------------------------------------------------------------ */
/* EXPORT                                                              */
/* ------------------------------------------------------------------ */

function csvCell(v) {
  if (v === null || v === undefined) return '';
  const s = Array.isArray(v) ? v.join('; ') : String(v);
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

/* columns: [{ key, label, csv?(row) }]. csv() lets a column export the
   underlying value where the cell renders a pill, an icon or a control -
   "Not measured" is what the screen should say and null is what a
   spreadsheet should receive. */
export function toCsv(rows, columns) {
  const head = columns.map((c) => csvCell(c.label)).join(',');
  const body = (rows || []).map((r) =>
    columns.map((c) => csvCell(typeof c.csv === 'function' ? c.csv(r) : r[c.key])).join(',')
  );
  return [head].concat(body).join('\r\n');
}

export function downloadCsv(name, csv) {
  /* BOM, because Excel reads a UTF-8 CSV without one as Latin-1 and
     turns every accented name in the estate into mojibake. */
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/* rows are the rows ON SCREEN - already filtered and sorted by the
   caller. columns are the VISIBLE ones. Both are the caller's job
   precisely so that this cannot quietly export more than the person
   was looking at. */
export function ExportCsv({ rows, columns, name = 'export', label = 'Export CSV', disabled }) {
  const n = (rows || []).length;
  function go() {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(name + '_' + stamp + '.csv', toCsv(rows, columns));
  }
  return (
    <button
      className="btn outline"
      style={{ height: 32, padding: '0 12px', fontSize: 12 }}
      onClick={go}
      disabled={disabled || n === 0}
      title={n === 0 ? 'Nothing to export' : ('Export the ' + n + (n === 1 ? ' row' : ' rows') + ' currently shown')}
    >
      <Download size={14} />{label}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* TOOLBAR                                                             */
/* ------------------------------------------------------------------ */

/* The row above a table: filter on the left, controls on the right, the
   same way round on every table so nobody has to look for them twice. */
export function TableToolbar({ left, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
      {left}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        {children}
      </div>
    </div>
  );
}